// HTTP client — ky instance with a bearer interceptor and a single-flight 401 refresh.
//
// Task 326.18 (findings WEB-001 / X-005). What this replaces: every 401 started its OWN refresh
// with the same token. The server rotates on use, so when a page mounted several queries at once
// only the first refresh could succeed and the rest fell into `catch → logout()` — an operator
// logged out mid-task with a perfectly fresh session. Refreshes now go through refreshSession(),
// which is shared, and a failure ends the session visibly instead of retrying a dead credential.

import ky, { type Options } from 'ky';
import { useAuthStore } from '../stores/authStore';
import { endSession, refreshSession } from './session';

const RETRY_HEADER = 'X-Auth-Retry';

const apiClient = ky.create({
  prefixUrl: import.meta.env.VITE_API_BASE_URL,
  timeout: 30_000,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().token;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status !== 401) return response;

        // Already retried once with a freshly minted token: the 401 is about authorization, not
        // expiry. Retrying again would only loop.
        if (request.headers.get(RETRY_HEADER)) {
          useAuthStore.getState().logout();
          endSession('expired');
          return response;
        }

        try {
          const session = await refreshSession();
          // Same user, same tenant: keep the cached permission codes rather than blanking the menus
          // for a beat on every silent renewal (task 326.17).
          useAuthStore.getState().applySession(session, { keepPermissions: true });

          const retryHeaders = new Headers(request.headers);
          retryHeaders.set('Authorization', `Bearer ${session.accessToken}`);
          retryHeaders.set(RETRY_HEADER, '1');

          return fetch(new Request(request, { headers: retryHeaders }));
        } catch {
          // No usable session. One visible redirect for however many requests failed at once.
          useAuthStore.getState().logout();
          endSession('expired');
          return response;
        }
      },
    ],
  },
});

export default apiClient;

// Typed convenience wrappers used by endpoint modules.
// Errors propagate as ky HTTPError; normalize at call sites via normalizeError().
export const api = {
  get: <T>(url: string, options?: Options): Promise<T> => apiClient.get(url, options).json<T>(),

  post: <T>(url: string, options?: Options): Promise<T> => apiClient.post(url, options).json<T>(),

  put: <T>(url: string, options?: Options): Promise<T> => apiClient.put(url, options).json<T>(),

  patch: <T>(url: string, options?: Options): Promise<T> => apiClient.patch(url, options).json<T>(),

  del: (url: string, options?: Options): Promise<void> =>
    apiClient.delete(url, options).then(() => undefined),
};
